import {
  Accordion,
  Checkbox,
  PlusIcon,
  SecondaryButton,
  SelectionInputLayout,
  notification,
  toast,
} from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  type EmailWhitelistPolicy,
  ItemListAction,
  useUpdateEmailWhitelistPolicyMutation,
} from 'src/generated/graphql-manager';

import { Whitelist } from '../whitelist';
import { WhitelistModal } from '../whitelist-modal';

import styles from '../event-access.module.css';

type Props = {
  emailWhitelistPolicy?: EmailWhitelistPolicy;
  formDisabled?: boolean;
  refetch: () => void;
};

export function EmailWhitelistPolicy({ emailWhitelistPolicy, refetch, formDisabled }: Props) {
  const { t } = useTranslation();
  const { webcastId = '' } = useParams();

  const [emailWhitelistModalOpen, setEmailWhitelistModalOpen] = useState(false);

  const emailWhitelistEnabled = emailWhitelistPolicy?.enabled ?? false;
  const whitelistedEmails = emailWhitelistPolicy?.emails || [];
  const numberOfAttendees = whitelistedEmails.length;

  const [updateEmailWhitelistPolicyMutation, { loading: updateEmailWhitelistPolicyLoading }] =
    useUpdateEmailWhitelistPolicyMutation();

  const updateEmailWhitelistPolicy = ({ enabled }: { enabled?: boolean }) => {
    if (updateEmailWhitelistPolicyLoading) return;

    updateEmailWhitelistPolicyMutation({
      variables: {
        input: {
          webcastId,
          enabled: enabled ?? null,
          emails: null,
        },
      },
      onCompleted: (data) => {
        refetch();

        if (data?.updateEmailWhitelistPolicy.__typename === 'UpdateEmailWhitelistPolicySuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const addWhitelistEmails = (items: string[]) => {
    if (updateEmailWhitelistPolicyLoading) return;

    updateEmailWhitelistPolicyMutation({
      variables: {
        input: {
          webcastId,
          enabled: null,
          emails: [
            {
              action: ItemListAction.ADD,
              items: items.map((item) => item.toLowerCase()),
            },
          ],
          requireEmailVerification: null,
        },
      },
      onCompleted: (data) => {
        refetch();

        if (data?.updateEmailWhitelistPolicy.__typename === 'UpdateEmailWhitelistPolicySuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const removeWhitelistEmails = (items: string[]) => {
    if (updateEmailWhitelistPolicyLoading) return;

    updateEmailWhitelistPolicyMutation({
      variables: {
        input: {
          webcastId,
          enabled: null,
          emails: [
            {
              action: ItemListAction.REMOVE,
              items,
            },
          ],
          requireEmailVerification: null,
        },
      },
      onCompleted: (data) => {
        refetch();

        if (data?.updateEmailWhitelistPolicy.__typename === 'UpdateEmailWhitelistPolicySuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  return (
    <>
      <SelectionInputLayout
        variant="ghost"
        checked={emailWhitelistEnabled}
        disabled={formDisabled}
        hintMessage={t('manager.webcastSetup.eventAccess.accessControlMethods.emailWhitelist.hint')}
        inputElement={
          <Checkbox
            id="email-whitelist-policy"
            data-testid="email-whitelist-policy-checkbox"
            checked={emailWhitelistEnabled}
            disabled={formDisabled}
            onChange={() => updateEmailWhitelistPolicy({ enabled: !emailWhitelistEnabled })}
          >
            {t('manager.webcastSetup.eventAccess.accessControlMethods.emailWhitelist.label')}
          </Checkbox>
        }
      >
        <div className={styles.attendeesWrapper}>
          <SecondaryButton
            data-testid="email-whitelist-add-attendees-button"
            type="button"
            small
            onClick={() => setEmailWhitelistModalOpen(true)}
          >
            {t('manager.webcastSetup.eventAccess.accessControlMethods.emailWhitelist.addAttendees')}
            <PlusIcon />
          </SecondaryButton>

          {numberOfAttendees === 0 && (
            <span className={styles.accordionLabel} data-testid="email-whitelist-attendees-count">
              {t('manager.webcastSetup.eventAccess.accessControlMethods.emailWhitelist.attendees', {
                count: numberOfAttendees,
              })}
            </span>
          )}

          {numberOfAttendees > 0 && (
            <Accordion
              data-testid="email-whitelist-attendees-accordion"
              className={styles.accordionAttendees}
              buttonContent={
                <span className={styles.accordionLabel}>
                  {t('manager.webcastSetup.eventAccess.accessControlMethods.emailWhitelist.attendees', {
                    count: numberOfAttendees,
                  })}
                </span>
              }
            >
              <Whitelist
                type="emails"
                items={whitelistedEmails}
                onRemove={(item) => removeWhitelistEmails([item])}
                onRemoveAll={() => removeWhitelistEmails(whitelistedEmails)}
              />
            </Accordion>
          )}
        </div>
      </SelectionInputLayout>

      <WhitelistModal
        type="emails"
        isOpen={emailWhitelistModalOpen}
        onConfirm={addWhitelistEmails}
        onClose={() => setEmailWhitelistModalOpen(false)}
      />
    </>
  );
}
